import React from 'react';

// Helper function to convert hex to rgb
const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '107, 114, 128';
};

// User-Readable Streaming Events Display Component
const StreamingEventsDisplay = ({ events, onAnalysisComplete }) => {
    const [isAnalysisFinished, setIsAnalysisFinished] = React.useState(false);
    const scrollRef = React.useRef(null);
    const [isAutoScrollEnabled, setIsAutoScrollEnabled] = React.useState(true);
    const [isUserScrolling, setIsUserScrolling] = React.useState(false);

    // Auto-scroll to bottom when new events arrive (more aggressive auto-follow)
    React.useEffect(() => {
        if (scrollRef.current && events.length > 0) {
            const scrollElement = scrollRef.current;
            const scrollHeight = scrollElement.scrollHeight;
            const clientHeight = scrollElement.clientHeight;
            const scrollTop = scrollElement.scrollTop;
            const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
            const isNearBottom = distanceFromBottom <= 100; // 100px tolerance for auto-scroll

            // Always auto-scroll for new events unless user has scrolled significantly up
            if (isAutoScrollEnabled || isNearBottom) {
                // Use requestAnimationFrame for better performance
                requestAnimationFrame(() => {
                    scrollElement.scrollTo({
                        top: scrollHeight - clientHeight,
                        behavior: 'smooth'
                    });
                });
                setIsAutoScrollEnabled(true);
                setIsUserScrolling(false);
            }
        }
    }, [events, isAutoScrollEnabled]);

    // Handle user scroll interactions - only disable auto-scroll if user scrolls significantly up
    const handleScroll = React.useCallback(() => {
        if (!scrollRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
        const isAtBottom = distanceFromBottom <= 10; // 10px tolerance
        const hasScrolledAway = distanceFromBottom > 100; // More than 100px from bottom

        if (isAtBottom) {
            // Re-enable auto-scroll when back at bottom
            setIsAutoScrollEnabled(true);
            setIsUserScrolling(false);
        } else if (hasScrolledAway) {
            // Disable auto-scroll only if significantly scrolled up
            setIsAutoScrollEnabled(false);
            setIsUserScrolling(true);
        }
        // If between 10-100px from bottom, keep current auto-scroll state
    }, []);

    // Re-enable auto-scroll when user reaches bottom
    React.useEffect(() => {
        const scrollElement = scrollRef.current;
        if (scrollElement) {
            scrollElement.addEventListener('scroll', handleScroll);
            return () => scrollElement.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    // Check if analysis is complete and trigger action
    React.useEffect(() => {
        if (isAnalysisFinished) return; // Prevent multiple calls

        const hasCompleteEvent = events.some(event => event.event.event_type === 'complete');
        const hasFinalizeEvent = events.some(event => event.event.event_type === 'finalize_answer');

        if (hasCompleteEvent && hasFinalizeEvent) {
            setIsAnalysisFinished(true);
            onAnalysisComplete(); // Automatically trigger the report fetch
        }
    }, [events, onAnalysisComplete, isAnalysisFinished]);

    // Handle analysis_initialized event to potentially store analysis_id
    React.useEffect(() => {
        const initEvent = events.find(event => event.event.event_type === 'analysis_initialized');
        if (initEvent && initEvent.event.data?.analysis_id) {
            console.log('Analysis initialized with ID:', initEvent.event.data.analysis_id);
            // The analysis_id will be extracted from the complete event when needed
        }
    }, [events]);

    // Filter and format events for user readability
    const getUserReadableEvents = () => {
        return events.filter(event => {
            const eventType = event.event.event_type;
            return ['analysis_started', 'generate_query', 'web_research', 'reflection'].includes(eventType);
        }).map(event => {
            const eventType = event.event.event_type;
            let userContent = '';

            switch (eventType) {
                case 'analysis_started':
                    userContent = event.event.data?.message || 'Analysis started';
                    break;

                case 'generate_query':
                    if (event.event.data?.queries_generated && Array.isArray(event.event.data.queries_generated)) {
                        // Clean and show all queries without special characters
                        const allQueries = event.event.data.queries_generated
                            .map(q => q.query?.replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim())
                            .filter(Boolean);
                        if (allQueries.length > 0) {
                            userContent = allQueries.join('\n');
                        }
                    }
                    break;

                case 'web_research':
                    if (event.event.data?.sources_gathered && Array.isArray(event.event.data.sources_gathered)) {
                        // Show ALL sources with title and URL
                        const sources = event.event.data.sources_gathered;
                        userContent = sources.map(source =>
                            `${source.title}: ${source.url}`
                        ).join('\n');
                    }
                    break;

                case 'reflection':
                    const gap = event.event.data?.knowledge_gap || '';
                    const assessment = event.event.data?.assessment || '';
                    userContent = `Knowledge Gap: ${gap}\nAssessment: ${assessment}`;
                    break;

                default:
                    userContent = 'Processing...';
            }

            return {
                ...event,
                userContent,
                eventType
            };
        });
    };

    const userEvents = getUserReadableEvents();

    // Get event type styling
    const getEventStyle = (eventType) => {
        const styles = {
            'analysis_started': { color: '#10b981', icon: '🚀', title: 'Analysis Started' },
            'generate_query': { color: '#3b82f6', icon: '🔍', title: 'Research Queries' },
            'web_research': { color: '#f59e0b', icon: '🌐', title: 'Web Research' },
            'reflection': { color: '#ec4899', icon: '🤔', title: 'Knowledge Assessment' }
        };
        return styles[eventType] || { color: '#6b7280', icon: '📝', title: 'Processing' };
    };

    if (isAnalysisFinished) {
        return (
            <div className="streaming-events-container">
                <div className="analysis-complete-container">
                    <div className="complete-icon">✅</div>
                    <h3 className="complete-title">Analysis Complete!</h3>
                    <p className="complete-message">
                        Finalizing and loading your comprehensive market analysis report...
                    </p>
                    <div className="loading-spinner-small" style={{ margin: '20px auto' }}></div>
                </div>
            </div>
        );
    }

    return (
        <div className="streaming-events-container">
            <div className="streaming-header">
                <h3 className="streaming-events-title">
                    <span className="live-indicator">🔴</span>
                    LIVE: AI Market Analysis
                </h3>
                {isUserScrolling && (
                    <div className="scroll-indicator">
                        <span className="scroll-hint">👆 Scroll to resume auto-follow</span>
                    </div>
                )}
            </div>

            <div className="streaming-events-list" ref={scrollRef}>
                {userEvents.map((eventItem, index) => {
                    const eventStyle = getEventStyle(eventItem.eventType);
                    return (
                        <div
                            key={eventItem.id}
                            className="streaming-event-item user-readable"
                            style={{
                                borderLeftColor: eventStyle.color,
                                background: `rgba(${hexToRgb(eventStyle.color)}, 0.1)`,
                                animationDelay: `${index * 0.2}s`
                            }}
                        >
                            <div className="event-header">
                                <div className="event-type-badge" style={{ background: eventStyle.color }}>
                                    <span className="event-icon">{eventStyle.icon}</span>
                                    <span className="event-type-text">{eventStyle.title}</span>
                                </div>
                            </div>

                            <div className="event-content user-content">
                                <div className="user-readable-text">
                                    {eventItem.userContent.split('\n').map((line, lineIndex) => (
                                        <div key={lineIndex} className="content-line">
                                            {line}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="streaming-footer">
                <div className="stream-stats">
                    <span className="processing-status">🔄 AI is analyzing your market data...</span>
                    <span className="total-events">📊 {events.length} events processed</span>
                    <span className="stream-status">
                        {isAutoScrollEnabled ? '🔄 Auto-following' : '⏸️ Paused'}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default StreamingEventsDisplay;
